import React, { useEffect, useRef, useState } from 'react';
import { useScrolledToBottom } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { SaviyntRenderQuillDelta } from '../../../../../shared/components';
import { testIds, TOKENS } from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { type QuillDelta } from '../../../../../shared/types';
import { useResponsiveLayout } from '../../../../FormPreview/hooks';

import { termsAndConditionsModalMessages } from './messages';

import 'quill/dist/quill.snow.css';
import styles from '../../../../FormPreview/components/PreviewFields/PreviewFields.module.css';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  richText: QuillDelta | undefined;
}

function TermsAndConditionsModal({
  isOpen,
  onClose,
  onAccept,
  richText,
}: TermsAndConditionsModalProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  );
  const [hasUserFinishedScrolling, setHasUserFinishedScrolling] =
    useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const l = useInvertedLocalize(termsAndConditionsModalMessages);

  const { hasReachedBottom } = useScrolledToBottom(scrollElement, 48);
  const { isMobileXlargeWidth } = useResponsiveLayout();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isOpen && sectionRef.current) {
      setScrollElement(sectionRef.current);
    }

    if (isOpen) {
      const timer = setTimeout(() => {
        if (sectionRef.current) {
          setScrollElement(sectionRef.current);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (hasReachedBottom) {
      setHasUserFinishedScrolling(true);
    }
  }, [hasReachedBottom]);

  const contentBody = (
    <>
      {!richText && (
        <Typography
          kind='body2'
          color={TOKENS.CF_NEU_STL}
          className={styles.termsAndConditionsEmptyDelta}>
          {l(
            'No content, please fill out your terms and conditions from the properties panel'
          )}
        </Typography>
      )}
      <SaviyntRenderQuillDelta delta={richText} confirmExternalLinks />
    </>
  );

  const footerActions = (
    <FooterActionBar size='large' className={styles.termsAndConditionsFooter}>
      <Button
        kind='ghost'
        onClick={onClose}
        dataTestId={testIds.FF_TNC_CONDITIONS_MODAL_CANCEL_BUTTON}>
        {l('Cancel')}
      </Button>
      <Button
        kind='filled'
        isDisabled={!hasUserFinishedScrolling}
        onClick={onAccept}
        dataTestId={testIds.FF_TNC_CONDITIONS_MODAL_ACCEPT_BUTTON}>
        {l('Accept')}
      </Button>
    </FooterActionBar>
  );

  const headerComp = (
    <Box tag='header' className={styles.termsAndConditionsHeader}>
      <Typography kind='h2' className={styles.termsAndConditionsHeaderTitle}>
        {l('Terms & Conditions')}
      </Typography>
      <ButtonIcon
        size='medium'
        kind='ghost'
        ariaLabel={l('Close')}
        icon={<Icon kind='Close' color='neutral-1000' size='smallMedium' />}
        onClick={onClose}
      />
    </Box>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      isMobileWidth={isMobileXlargeWidth}
      HeaderComp={headerComp}
      contentBody={contentBody}
      sectionRef={sectionRef}
      FooterActionBarComp={footerActions}
      className={styles.termsAndConditionsModal}
      classNames={{ bodyClassName: styles.termsAndConditionsBody }}
      dataTestId={testIds.FF_TNC_CONDITIONS_MODAL}
    />
  );
}

export default TermsAndConditionsModal;
